import React from 'react'
import { makeStyles, Popover, IconButton, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import Sms from '@material-ui/icons/Sms'
import RoomIcon from '@material-ui/icons/Room'
import MailIcon from '@material-ui/icons/Mail'
import PhoneIcon from '@material-ui/icons/Phone'
import AddIcon from '@material-ui/icons/Add'
import StarIcon from '@material-ui/icons/Star'
import classNames from 'classnames'

const useStyles = makeStyles<Theme, ContactIconProps>(
  (theme) => ({
    root: {},
    contactIconDiv: {
      padding: theme.spacing(1),
      paddingTop: 0,
      paddingBottom: 0
    },
    contactButton: {
      backgroundColor: theme.paintscout.palette.primary,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing(1)
    },
    contactBackground: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.primary
      }
    },
    preferredStar: {
      position: 'absolute',
      top: -10,
      right: -10,
      color: '#f5a622',
      border: theme.palette.common.white
    },
    contactIcon: {
      color: theme.palette.common.white
    },
    outlinedContactIcon: {
      color: theme.paintscout.palette.primary
    },
    disabledButton: {
      backgroundColor: theme.paintscout.palette.light,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.light
      }
    },
    popover: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows[1]
    },
    outlined: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.paintscout.palette.primary}`
    }
  }),
  { name: 'ContactIcon' }
)

export interface ContactIconProps {
  classes?: StyleClasses<typeof useStyles>
  preferred?: boolean
  disabled?: boolean
  outlined?: boolean
  type: string
  onClick?: () => void
}

function ContactIcon(props: ContactIconProps) {
  const classes = useStyles(props)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const { preferred, type, disabled, outlined, onClick } = props
  let Icon = AddIcon
  switch (type) {
    case 'address':
      Icon = RoomIcon
      break
    case 'phone':
      Icon = PhoneIcon
      break
    case 'email':
      Icon = MailIcon
      break
    case 'sms':
      Icon = Sms
      break
    default:
      Icon = AddIcon
      break
  }

  function handleDisabledClick(event) {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const iconName = type.charAt(0).toUpperCase() + type.slice(1)
  return (
    <div className={classes.contactIconDiv}>
      <IconButton
        aria-describedby={id}
        className={classNames({
          [classes.contactButton]: true,
          [classes.disabledButton]: disabled,
          [classes.outlined]: outlined,
          [classes.contactBackground]: !disabled && type != 'add'
        })}
        onClick={disabled ? handleDisabledClick : onClick}
      >
        <Icon className={classNames({ [classes.contactIcon]: true, [classes.outlinedContactIcon]: outlined })} />
        {preferred && <StarIcon className={classes.preferredStar} />}
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <div className={classes.popover}>
          <Typography variant="body1">
            No{' '}
            {(['Sms', 'Phone'].includes(iconName)
              ? 'phone number'
              : iconName == 'Add'
                ? 'actions'
                : iconName
            ).toLowerCase()}{' '}
            found.
          </Typography>
        </div>
      </Popover>
    </div>
  )
}

export default ContactIcon
